import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import FactoryFlag, { countryOfFactory } from '../../components/factory/FactoryFlag';
import MachineMiniCard from '../../components/factory/MachineMiniCard';
import { formatHeaderTime } from '../../components/factory/FactoryPageHeader';
import { fetchCompanyFactoryOverview } from '../../services/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { useTranslation } from '../../context/LanguageContext';
import './EnterpriseFactoryOverview.css';

/* ===== 전사 모니터링 > 공장별 사출 현황(공장별 대시보드) — PNG 시안 =====
 * 헤더: 전사 › 공장별 사출 현황 + 동기화 시각 · 새로 고침
 * 1행 KPI 4칸: 전체 설비 수 · 전체 가동률 · 전체 알람 · 전체 생산량(오늘), 상태 배지
 * 공장마다 한 줄: [공장 요약(국기·코드·상태, 가동 비율 링·가동/대기/정지, 가동률·설비 수·미처리 알람)]
 *                [설비 현황(사출기 미니 카드 3열)] [실시간 알람(전체/위험/경고 필터, 같은 설비·같은 알람은 묶어 횟수 표시)]
 *
 * 데이터: /dashboard/company-factory — 공장별 equipment(가동/대기/정지), operationRate(가동률), production(오늘 샷 수),
 *         alarms(미처리 알람 수·심각도별), machines(상태·샷카운트·제품=금형번호·알람 수), recentAlarms(최근 24시간 알람).
 * 시안의 "전일 대비" 증감은 일별 비교 데이터가 없어 넣지 않았다. 배지 기준은 화면 상수(BADGE). 스타일은 efo- 접두어. */

const BADGE = {
  utilization: { good: 80, warn: 50 },      /* 전체 가동률 % */
  stoppedShare: { good: 10, warn: 30 }      /* 정지 설비 비율 % (낮을수록 좋음) */
};

const isNum = (v) => v != null && v !== '' && !Number.isNaN(Number(v));
const num = (v) => (isNum(v) ? Number(v) : null);
const fmt = (v, d = 1) => (v == null ? '—' : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtInt = (v) => (v == null ? '—' : Math.round(Number(v)).toLocaleString());
const COUNTRY_EN = { KR: 'Korea', VN: 'Vietnam' };

function Badge({ level, label }) {
  if (!level) return null;
  return <span className={`efo-badge efo-badge-${level}`}>{label}</span>;
}

/* 가동/대기/정지 비율 링 */
function StatusRing({ running, idle, stopped, label }) {
  const total = running + idle + stopped;
  const r = 70;
  const c = 2 * Math.PI * r;
  const segs = total > 0
    ? [{ v: running, cls: 'run' }, { v: idle, cls: 'idle' }, { v: stopped, cls: 'stop' }]
    : [];
  let offset = 0;
  const pct = total > 0 ? (running / total) * 100 : null;
  return (
    <div className="efo-ring">
      <svg viewBox="0 0 168 168" width="168" height="168">
        <circle cx="84" cy="84" r={r} className="efo-ring-track" />
        {segs.map((s) => {
          const len = (s.v / total) * c;
          const el = len > 0 ? (
            <circle key={s.cls} cx="84" cy="84" r={r} className={`efo-ring-seg efo-ring-${s.cls}`}
              strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} transform="rotate(-90 84 84)" />
          ) : null;
          offset += len;
          return el;
        })}
      </svg>
      <div className="efo-ring-text">
        <b>{pct == null ? '—' : pct.toFixed(1)}<small>{pct == null ? '' : '%'}</small></b>
        <span>{label}</span>
      </div>
    </div>
  );
}

function EnterpriseFactoryOverview() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tr = useCallback((k, p) => t(`pages.overview.enterpriseFactory.v2.${k}`, p), [t]);

  const [factories, setFactories] = useState([]);
  const [syncTime, setSyncTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [alarmFilter, setAlarmFilter] = useState({});   /* { factoryId: 'all' | 'error' | 'warning' } */

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const data = await fetchCompanyFactoryOverview();
      setFactories(data?.factories || []);
      setSyncTime(data?.lastSyncTime || new Date());
      setError(null);
    } catch (err) {
      console.error('공장 현황 조회 실패:', err);
      if (!silent) setError(tr('loadFailed'));
    } finally {
      if (silent) setTimeout(() => setRefreshing(false), 600); else setLoading(false);
    }
  }, [tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true));

  /* ---- 공장별 가공 ---- */
  const rows = useMemo(() => factories.map((f) => {
    const eq = f.equipment || {};
    const running = num(eq.running) || 0;
    const idle = num(eq.idle) || 0;
    const stopped = num(eq.stopped) || 0;
    const total = num(eq.total) ?? running + idle + stopped;
    const state = !total ? 'none' : stopped === 0 ? 'running' : stopped === total ? 'stopped' : 'partial';

    /* 실시간 알람: 미해결만, 같은 설비·같은 코드는 묶고 횟수를 센다 (응답은 최근 24시간) */
    const active = (f.recentAlarms || []).filter((a) => !a.status || String(a.status).toUpperCase() === 'ACTIVE');
    const groups = new Map();
    active.forEach((a) => {
      const key = `${a.equipmentId}|${a.alarmId}|${a.message}`;
      const g = groups.get(key);
      if (g) { g.count += 1; if (String(a.time) > String(g.time)) g.time = a.time; }
      else groups.set(key, { ...a, count: 1 });
    });
    const grouped = Array.from(groups.values()).sort((a, b) => {
      const sev = (s) => (s === 'error' ? 0 : s === 'warning' ? 1 : 2);
      return sev(a.severity) - sev(b.severity) || String(b.time).localeCompare(String(a.time));
    });

    return {
      id: f.factoryId,
      name: f.factoryName,
      code: f.factoryCode,
      country: countryOfFactory(f.factoryName, f.location),
      running, idle, stopped, total, state,
      rate: num(f.operationRate),
      production: num(f.production),
      openAlarms: num(f.alarms?.total),
      critical: num(f.alarms?.error),
      warning: num(f.alarms?.warning),
      info: num(f.alarms?.info),
      machines: f.machines || [],
      alarmCounts: {
        all: active.length,
        error: active.filter((a) => a.severity === 'error').length,
        warning: active.filter((a) => a.severity === 'warning').length
      },
      alarms: grouped
    };
  }), [factories]);

  /* ---- 상단 KPI ---- */
  const kpi = useMemo(() => {
    const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);
    const total = sum('total');
    const weightedRate = total > 0
      ? rows.reduce((s, r) => s + (r.rate != null ? r.rate * (r.total || 0) : 0), 0) / total
      : null;
    return {
      total, running: sum('running'), idle: sum('idle'), stopped: sum('stopped'),
      rate: weightedRate,
      alarms: sum('openAlarms'), critical: sum('critical'), warning: sum('warning'), info: sum('info'),
      production: rows.some((r) => r.production != null) ? sum('production') : null
    };
  }, [rows]);

  const level = {
    equipment: kpi.total ? ((kpi.stopped / kpi.total) * 100 <= BADGE.stoppedShare.good ? 'good' : (kpi.stopped / kpi.total) * 100 <= BADGE.stoppedShare.warn ? 'warn' : 'bad') : null,
    rate: kpi.rate == null ? null : kpi.rate >= BADGE.utilization.good ? 'good' : kpi.rate >= BADGE.utilization.warn ? 'warn' : 'bad',
    alarm: kpi.critical > 0 ? 'bad' : kpi.warning > 0 ? 'warn' : 'good'
  };
  const levelLabel = (l) => tr(`level.${l}`);
  const statusLabel = (s) => tr(`status.${['running', 'idle', 'stopped', 'partial'].includes(s) ? s : 'none'}`);
  const goMachine = (m, openTab) => navigate('/machine', { state: { machineData: { id: m.equipmentId, name: m.equipmentName }, ...(openTab ? { openTab } : {}) } });

  return (
    <MainLayout>
      <div className="efo-page">
        {/* ---- 헤더 ---- */}
        <div className="efo-header">
          <div className="efo-crumb">
            <span className="material-symbols-outlined efo-crumb-icon">apartment</span>
            <span className="efo-crumb-root">{tr('breadcrumbRoot')}</span>
            <span className="material-symbols-outlined efo-crumb-sep">chevron_right</span>
            <span className="efo-crumb-page">{t('sidebar.enterprise.factoryOverview')}</span>
          </div>
          <div className={`efo-header-right ${refreshing ? 'refreshing' : ''}`}>
            <span className="efo-time">{formatHeaderTime(syncTime)}</span>
            <button type="button" className="efo-refresh" onClick={() => load(true)} title={tr('refresh')} aria-label={tr('refresh')}>
              <span className="material-symbols-outlined">refresh</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="efo-error">{error}<button type="button" onClick={() => load(false)}>{tr('retry')}</button></div>
        )}

        {/* ---- KPI 4칸 ---- */}
        <div className="efo-kpis">
          <div className="efo-kpi">
            <div className="efo-kpi-head"><span>{tr('kpi.equipment')}</span><Badge level={level.equipment} label={levelLabel(level.equipment)} /></div>
            <div className="efo-kpi-value">{fmtInt(kpi.total)}<small>{tr('unitMachine')}</small></div>
            <div className="efo-kpi-sub">{rows.map((r) => `${r.name} ${fmtInt(r.total)}`).join(' · ') || '—'}</div>
          </div>
          <div className="efo-kpi">
            <div className="efo-kpi-head"><span>{tr('kpi.rate')}</span><Badge level={level.rate} label={levelLabel(level.rate)} /></div>
            <div className="efo-kpi-value">{fmt(kpi.rate)}<small>%</small></div>
            <div className="efo-kpi-sub">{tr('kpi.rateSub', { r: kpi.running, i: kpi.idle, s: kpi.stopped })}</div>
          </div>
          <div className="efo-kpi">
            <div className="efo-kpi-head"><span>{tr('kpi.alarms')}</span><Badge level={level.alarm} label={levelLabel(level.alarm)} /></div>
            <div className="efo-kpi-value">{fmtInt(kpi.alarms)}<small>{tr('unitCase')}</small></div>
            <div className="efo-kpi-sub">{tr('kpi.alarmSub', { c: kpi.critical, w: kpi.warning, i: kpi.info })}</div>
          </div>
          <div className="efo-kpi">
            <div className="efo-kpi-head"><span>{tr('kpi.production')}</span></div>
            <div className="efo-kpi-value">{fmtInt(kpi.production)}</div>
            <div className="efo-kpi-sub">{tr('kpi.productionSub')}</div>
          </div>
        </div>

        {loading && rows.length === 0 && <div className="efo-message">{tr('loading')}</div>}
        {!loading && !error && rows.length === 0 && <div className="efo-message">{tr('empty')}</div>}

        {/* ---- 공장별 줄 ---- */}
        {rows.map((f) => {
          const filter = alarmFilter[f.id] || 'all';
          const list = filter === 'all' ? f.alarms : f.alarms.filter((a) => a.severity === filter);
          return (
            <section key={f.id} className="efo-factory">
              {/* 공장 요약 */}
              <div className="efo-summary">
                <button type="button" className="efo-summary-head" onClick={() => navigate(`/factory?factoryId=${f.id}`)} title={tr('openFactory')}>
                  <FactoryFlag country={f.country} cls="efo-flag" size={22} />
                  <span className="efo-factory-name">{f.name}</span>
                  {f.country && <span className="efo-factory-en">{COUNTRY_EN[f.country]}</span>}
                  {f.code && <span className="efo-chip"><i />{f.code}</span>}
                  <span className={`efo-state efo-state-${f.state}`}>{statusLabel(f.state)}</span>
                </button>
                <div className="efo-ring-row">
                  <StatusRing running={f.running} idle={f.idle} stopped={f.stopped} label={tr('runShare')} />
                  <ul className="efo-ring-legend">
                    <li><i className="run" />{tr('legend.running')} {f.running}</li>
                    <li><i className="idle" />{tr('legend.idle')} {f.idle}</li>
                    <li><i className="stop" />{tr('legend.stopped')} {f.stopped}</li>
                  </ul>
                </div>
                <div className="efo-summary-stats">
                  <div><span>{tr('rate')}</span><b>{fmt(f.rate)}<small>%</small></b></div>
                  <div><span>{tr('equipment')}</span><b>{fmtInt(f.total)}<small>{tr('unitMachine')}</small></b></div>
                  <div><span>{tr('openAlarms')}</span><b>{fmtInt(f.openAlarms)}<small>{tr('unitCount')}</small></b></div>
                </div>
              </div>

              {/* 설비 현황 */}
              <div className="efo-machines">
                <h3 className="efo-col-title">{tr('machinesTitle')}</h3>
                {f.machines.length === 0 ? <div className="efo-empty">{tr('noMachines')}</div> : (
                  <div className="efo-machine-grid">
                    {f.machines.map((m, idx) => (
                      <MachineMiniCard
                        key={m.equipmentId}
                        name={m.equipmentName}
                        fallbackNo={idx + 1}
                        status={m.status}
                        alarms={m.alarms}
                        moldNo={m.productName}
                        shotCount={num(m.shotCount)}
                        moldLabel={tr('moldNo')}
                        shotLabel={tr('shotCount')}
                        cavity={m.cavityCount} /* GET /dashboard/company-factory → machines[].cavityCount */
                        cycleTime={m.cycleTime} /* 같은 응답 cycleTime (초) */
                        cavLabel={tr('cav')}
                        ctLabel={tr('ct')}
                        statusLabel={statusLabel(m.status)}
                        alarmTitle={tr('alarmCount', { n: m.alarms })}
                        title={[m.equipmentName, statusLabel(m.status), isNum(m.cycleTime) && Number(m.cycleTime) > 0 ? `${tr('cycle')} ${Number(m.cycleTime).toFixed(1)}s` : null].filter(Boolean).join(' · ')}
                        onClick={() => goMachine(m)}
                        onAlarmClick={() => goMachine(m, 'alarm')}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* 실시간 알람 */}
              <div className="efo-alarms">
                <div className="efo-alarm-head">
                  <h3 className="efo-col-title">{tr('alarmsTitle')}</h3>
                  <div className="efo-filters">
                    {['all', 'error', 'warning'].map((k) => (
                      <button key={k} type="button" className={`efo-filter efo-filter-${k} ${filter === k ? 'on' : ''}`} onClick={() => setAlarmFilter((v) => ({ ...v, [f.id]: k }))}>
                        {tr(`filter.${k}`)} <b>{f.alarmCounts[k]}</b>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="efo-alarm-list">
                  {list.length === 0 ? <div className="efo-empty">{tr('noAlarms')}</div> : list.map((a) => (
                    <button type="button" key={`${a.equipmentId}-${a.alarmId}-${a.message}`} className={`efo-alarm efo-alarm-${a.severity || 'info'}`} onClick={() => goMachine(a, 'alarm')}>
                      <div className="efo-alarm-top">
                        <span className="efo-alarm-equip">{a.equipmentName}</span>
                        <span className="efo-alarm-meta">{a.count > 1 ? tr('repeat', { n: a.count, time: a.time }) : a.time}</span>
                      </div>
                      <div className="efo-alarm-msg">{a.message || a.alarmId}</div>
                    </button>
                  ))}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </MainLayout>
  );
}

export default EnterpriseFactoryOverview;
